import type { CSSProperties } from 'react';
import cscLogo from '@/assets/images/logo/csc_logo_logo_only.svg';

/**
 * Login brand illustration — "Operations around the brand" (86d3buh1u →
 * 86d3c6vej → 86d3cff1t).
 *
 * DOP is the **Digital Operations Platform** for **Capitol Steel**, whose only
 * product is deformed (ribbed) reinforcing bar. The art:
 *   - The **core is the Capitol Steel logo mark** — the brand at the center.
 *   - The **connectors are ribbed bars** (a base stroke with evenly spaced
 *     transverse ticks = the deformations of rebar), so the network is literally
 *     built of the product.
 *   - The five **nodes are daily operations** the platform connects — order,
 *     store, move, deliver, oversee — NOT products (Capitol Steel makes only
 *     rebar; the glyphs must never imply coils, beams, or sheets).
 *
 * Theming: every stroke/fill is a background-surface semantic token
 * (`foreground` / `border` / `muted`), because the illustration now sits on
 * `bg-background` — NOT the sidebar surface — so it keeps contrast in light,
 * dark, and Industrial. The logo is the one exception — it is a brand-fixed
 * asset (red in every theme), so referencing it via SVG `<image>` (which is
 * CSS-isolated and cannot be themed) is correct here; it is the same mark used
 * in the wordmark. A solid `fill-background` disc sits under the core so the
 * converging connectors and inward pulses dissolve into it instead of leaking
 * past the logo.
 *
 * Motion: the inward data pulse travels via the `login-pulse-flow` keyframe
 * (src/styles/globals.css), each dot carrying its own travel vector through the
 * `--flow-dx/--flow-dy` custom properties. Every animated element also carries
 * `motion-reduce:animate-none`, so the illustration freezes under
 * `prefers-reduced-motion`.
 */

const CENTER = { x: 220, y: 180 };

type GlyphType = 'order' | 'store' | 'move' | 'deliver' | 'oversee';

interface OpsNode {
  x: number;
  y: number;
  glyph: GlyphType;
  /** Operation the platform connects — used only for the accessible description. */
  label: string;
}

// Five operations on a ring around the brand core (top / upper-right /
// lower-right / lower-left / upper-left). These are things the platform DOES,
// not products Capitol Steel makes.
const NODES: OpsNode[] = [
  { x: 220, y: 50, glyph: 'order', label: 'Order' },
  { x: 344, y: 140, glyph: 'store', label: 'Store' },
  { x: 296, y: 285, glyph: 'deliver', label: 'Deliver' },
  { x: 144, y: 285, glyph: 'move', label: 'Move' },
  { x: 96, y: 140, glyph: 'oversee', label: 'Oversee' },
];

/** Simple line-art glyph centered on (0,0). */
function renderGlyph(type: GlyphType) {
  switch (type) {
    case 'order': // document — placing / capturing an order
      return (
        <>
          <rect x={-8} y={-11} width={16} height={22} rx={2} />
          <line x1={-4} y1={-4} x2={4} y2={-4} />
          <line x1={-4} y1={1} x2={4} y2={1} />
          <line x1={-4} y1={6} x2={4} y2={6} />
        </>
      );
    case 'store': // stacked packages — warehousing the bars
      return (
        <>
          <rect x={-9} y={0} width={8} height={8} rx={1} />
          <rect x={1} y={0} width={8} height={8} rx={1} />
          <rect x={-4} y={-9} width={8} height={8} rx={1} />
        </>
      );
    case 'move': // opposing arrows — internal movement / transfer
      return (
        <>
          <line x1={-9} y1={-3} x2={7} y2={-3} />
          <path d="M4 -7 L8 -3 L4 1" />
          <line x1={9} y1={4} x2={-7} y2={4} />
          <path d="M-4 0 L-8 4 L-4 8" />
        </>
      );
    case 'deliver': // truck — getting the bars to the customer
      return (
        <>
          <rect x={-10} y={-6} width={13} height={10} rx={1} />
          <path d="M3 -2 L8 -2 L11 2 L3 2" />
          <circle cx={-5} cy={6} r={2} />
          <circle cx={7} cy={6} r={2} />
        </>
      );
    case 'oversee': // shield — administration / oversight of the platform
      return <path d="M0 -11 L8 -8 L8 -1 C8 5 4 9 0 11 C-4 9 -8 5 -8 -1 L-8 -8 Z" />;
  }
}

/** A ribbed (deformed) bar from the core out to a node: base stroke + ticks. */
function ribbedTicks(node: OpsNode) {
  const dx = node.x - CENTER.x;
  const dy = node.y - CENTER.y;
  const len = Math.hypot(dx, dy);
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy; // unit perpendicular
  const py = ux;
  const half = 4;
  const ticks: { x1: number; y1: number; x2: number; y2: number }[] = [];
  // Start past the core disc (r≈30) and stop before the node card (~22).
  for (let s = 44; s <= len - 30; s += 11) {
    const cx = CENTER.x + ux * s;
    const cy = CENTER.y + uy * s;
    ticks.push({
      x1: cx - px * half,
      y1: cy - py * half,
      x2: cx + px * half,
      y2: cy + py * half,
    });
  }
  return ticks;
}

export function LoginIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 440 360"
      fill="none"
      role="img"
      aria-label="Capitol Steel Digital Operations Platform — the Capitol Steel mark cradled by deformed reinforcing bar, with daily operations connected around it"
      className={className}
    >
      {/* Ambient brand glow — the core's halo. Gentle breathing pulse, frozen
          under reduced motion. */}
      <g className="animate-pulse motion-reduce:animate-none">
        <circle cx={CENTER.x} cy={CENTER.y} r={94} className="fill-primary/5" />
        <circle cx={CENTER.x} cy={CENTER.y} r={60} className="fill-primary/5" />
      </g>

      {/* Ribbed connectors — every operation wired to the core along a deformed
          bar (base stroke + transverse rib ticks). */}
      <g className="stroke-foreground/20" strokeWidth={1.5}>
        {NODES.map((n) => (
          <line key={`c-${n.x}-${n.y}`} x1={CENTER.x} y1={CENTER.y} x2={n.x} y2={n.y} />
        ))}
      </g>
      <g className="stroke-foreground/30" strokeWidth={1.5} strokeLinecap="round">
        {NODES.flatMap((n) =>
          ribbedTicks(n).map((t, i) => (
            <line key={`t-${n.x}-${n.y}-${i}`} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} />
          )),
        )}
      </g>

      {/* Inward data pulses — operations flowing through the platform. Each dot
          starts at its node and travels into the core (login-pulse-flow). */}
      {NODES.map((n, i) => (
        <circle
          key={`p-${n.x}-${n.y}`}
          cx={n.x}
          cy={n.y}
          r={3.5}
          className="fill-primary animate-[login-pulse-flow_2.8s_linear_infinite] motion-reduce:animate-none"
          style={
            {
              animationDelay: `${i * 0.56}s`,
              '--flow-dx': `${CENTER.x - n.x}px`,
              '--flow-dy': `${CENTER.y - n.y}px`,
            } as CSSProperties
          }
        />
      ))}

      {/* Operation nodes — cards + glyphs for what the platform connects. */}
      {NODES.map((n) => (
        <g key={`n-${n.x}-${n.y}`} transform={`translate(${n.x} ${n.y})`}>
          <rect
            x={-26}
            y={-22}
            width={52}
            height={44}
            rx={10}
            className="fill-muted stroke-border"
            strokeWidth={1.5}
          />
          <g
            className="stroke-foreground/70"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {renderGlyph(n.glyph)}
          </g>
        </g>
      ))}

      {/* Brand core — the Capitol Steel logo mark. The solid disc (page color)
          masks the converging connectors/pulses so they dissolve into the core
          instead of leaking past the logo. */}
      <circle
        cx={CENTER.x}
        cy={CENTER.y}
        r={30}
        className="fill-background stroke-primary/30"
        strokeWidth={1.5}
      />
      <image
        href={cscLogo}
        x={CENTER.x - 19}
        y={CENTER.y - 19}
        width={38}
        height={38}
      />
    </svg>
  );
}
